import { ElementRef, Injectable, TemplateRef } from '@angular/core';

import { AppinfoService, LbColumnType } from './appinfo.service';
import { TableDataPipe } from './../pipes/table-data.pipe';

import { LB } from './../../assets/TS/LBCore';
import { ModelMenuItemKey } from './main-data.service';
import { GivColumnDomain } from './domain.service';

export type TableCellAlign = 'C' | 'L' | 'R';

export interface TableConfig {
	rowH?: number; // row height
	// footerH?: number; // footer height
	headerH?: number; // header height
	pageSize?: number;
	messages?: {
		emptyMessage?: string;
		totalMessage: string;
	};
}
export interface TableColumn {
	prop: string;
	name: string;
	minWidth?: number;
	maxWidth?: number;
	width?: number;
	sortable?: boolean;
	type?: LbColumnType;
	isPrimKey?: boolean;
	isDisplay?: boolean;
	sourceTab?: string;
	seq?: number;
	pipe?: any;
	headerClass?: string | Function;
	cellClass?: string | Function;
	cellTemplate?: TemplateRef<any>;
	headerTemplate?: TemplateRef<any>;
	frozenLeft?: boolean;
	info: {
		tooltip?: string;
		schemaErrors?: string;
		skipSmartDisplay?: boolean;
		fixedCellClass: string[];
		// domain?: GivColumnDomain;
		[prop: string]: any;

		//
		standardColName?: string;
		// isStdSchema?: boolean;
		isJsonbCol?: boolean; // is packed in jsonb
		// jsonbType?: 'state' | 'nstd';
		// Editing attrs
		hideFromEditing?: boolean;
		isEditDisabled?: boolean;
		isUppercase?: boolean;
		isFishbone?: boolean;

		models?: ModelMenuItemKey[];
	}; // To store extra information for the column
}
export interface TableColumnOptions {
	skipGdoCheck?: boolean;
	hideColumn?: boolean;
	headerClass?: string;
	headerAlign?: TableCellAlign;
	width?: number;
	minWidth?: number;
	maxWidth?: number;
	align?: TableCellAlign;
	sortable?: boolean;
	cellTemplate?: TemplateRef<any>;
	headerTemplate?: TemplateRef<any>;
	isNameKeepCase?: boolean;
}

export interface TableRow {
	[rowName: string]: any;
}

@Injectable()
export class CommonTableService {

	// Default table config
	public defaultTableConfig: TableConfig = {
		rowH: 30,
		// footerH: 50,
		headerH: 30,
		pageSize: 20,
		messages: {
			emptyMessage: '',
			totalMessage: 'total'
		}
	};

	// For use of copying from table row
	private copyId = 'copyTableRowContent';

	constructor(
		private appinfoService: AppinfoService
	) { }

	// Get column width by the max length of data under that column
	getWidthByMaxStringLen(maxStringLen: number, colName?: string): number {
		const nameLen = colName ? colName.length : 0;
		maxStringLen = maxStringLen > nameLen ? maxStringLen : nameLen;
		let width = 20;
		if (maxStringLen > 15) {
			width = maxStringLen * 6 + 46;
		} else if (maxStringLen > 7) {
			width = maxStringLen * 9 + 42;
		} else {
			width = maxStringLen * 6 + 26;
		}
		return width;
	}
	setNewColumn(prop: string, name?: string, options?: TableColumnOptions): TableColumn {
		options = options || {};
		const column: TableColumn = {
			prop: prop,
			name: '',
			minWidth: 20,
			pipe: new TableDataPipe(),
			// cellClass: ' ',
			info: {
				fixedCellClass: []
			}
		};
		// Width passed in options has higher priority than maxStringLen
		if (options.width) {
			column.width = options.width;
		}
		if (options.minWidth) {
			column.minWidth = options.minWidth;
		}
		if (options.maxWidth) {
			column.maxWidth = options.maxWidth;
		}
		if (options.hideColumn) {
			column.isDisplay = false;
			this.hideColumn(column);
		}
		if (options.headerAlign) {
			this.addHeaderClass(column, 'cell-align-' + options.headerAlign);
		}
		if (options.headerClass) {
			this.addHeaderClass(column, options.headerClass);
		}
		if (options.headerTemplate) {
			column.headerTemplate = options.headerTemplate;
		}
		if (options.align) {
			const c = 'cell-align-' + options.align
			column.info.fixedCellClass.push(c);
			if (!options.headerAlign) {
				this.addHeaderClass(column, 'cell-align-' + options.align);
			}
		}
		if (options.hasOwnProperty('sortable')) {
			column.sortable = options.sortable;
		}
		if (options.cellTemplate) {
			column.cellTemplate = options.cellTemplate;
		}
		if (!options.skipGdoCheck) {
			const info = this.appinfoService.columnMap[prop];
			if (info) {
				name = name ? name : info.heading;
				column.type = info.type;
				// Is column primary key or not.
				column.isPrimKey = info.isPrimeKey;
				column.isDisplay = info.isDisplay;
				// Hide columns by adding 'hide-column' and 'hide-cell' classes.
				// Don't: change prop to 'NODISPLAY'. In some cases prop might be needed.
				if (!info.isDisplay) {
					this.hideColumn(column);
				}
				column.sourceTab = info.sourceTab;
				column.type = info.type;
				column.seq = info.seq;
			} else if (prop.indexOf('.') !== -1) {
				const parts = prop.split('.');
				if (this.appinfoService.columnMap[parts[0]]) {
					column.type = this.appinfoService.columnMap[parts[0]].type;
				}				column.name = parts[1];
				column.isDisplay = true;
			} else {
				// Display column if column doesn't have gdo
				column.isDisplay = true;
			}
		}
		if (!name) {
			name = prop;
		}
		name = options.isNameKeepCase ? name : name.toUpperCase();
		column.name = name;
		column.cellClass = this.fixedCellClassToString(column.info.fixedCellClass);
		return column;
	}
	setColumnsFromRows(rows) {
		const columns: TableColumn[] = [];
		if (rows.length) {
			// colsByProp will be used to calculate column alignment & size
			const colsByProp: {
				[prop: string]: { values: any[]; header: string; type: any; isDisplay: boolean; }
			} = {};
			rows.forEach((r) => {
				for (const prop of Object.keys(r)) {
					if (!colsByProp[prop]) {
						const propWithoutPrefix = this.getColPropWithoutPrefix(prop);
						const info = this.appinfoService.columnMap[propWithoutPrefix];
						let header = propWithoutPrefix;
						if (info && info.heading) {
							header = info.heading;
						}
						const type = info ? info.type : null;
						colsByProp[prop] = {
							values: [],
							header: header,
							type: type,
							isDisplay: info ? info.isDisplay : true
						};
					}
					colsByProp[prop].values.push(r[prop]);
				}
			});
			const numOfCols = Object.keys(colsByProp).length;
			for (const prop of Object.keys(colsByProp)) {
				const col = colsByProp[prop];
				let width;
				let align: TableCellAlign;
				if (col.isDisplay) {
					const display = LB.TableUtility.smartDisplay(col.values, col.header, col.type);
					if (display.size <= 4) { width = 50; }
					else if (display.size <= 20) { width = 50 + display.size * 7; }
					else if (display.size <= 50) {
						// This Is Very Rough Estimation!!! Should be improved.
						if (numOfCols <= 5) {
							width = display.size * 7;
						} else {
							width = 200;
						}
					} else {
						width = 300;
					}
					switch (display.alignment) {
						case 'center': align = 'C'; break;
						case 'left': align = 'L'; break;
						case 'right': align = 'R'; break;
					}
				}
				const column = this.setNewColumn(prop, col.header, {
					width: width,
					align: align
				});
				columns.push(column);
			}
		}
		return columns.sort(this.columnSort);
	}
	getColPropWithoutPrefix(prop): string {
		if (prop.indexOf('.') !== -1) {
			const parts = prop.split('.');
			return parts[1];
		} else {
			return prop;
		}
	}
	updateColumnHeaderAlign(column: TableColumn, align: TableCellAlign) {
		if (!column.headerClass || typeof(column.headerClass) === 'string') {
			let headerClasses = (<string>column.headerClass);
			const classPrefix = 'cell-align-';
			// Remove prev header align class
			if (headerClasses) {
				const splits = headerClasses.split(/[ ]+/);
				splits.forEach(s => {
					if (s.includes(classPrefix)) {
						headerClasses = headerClasses.replace(s, '');
					}
				});
			}
			column.headerClass = headerClasses;
			// Add align class
			this.addHeaderClass(column, classPrefix + align);
		}
	}
	addFixedCellClass(c: string, column: TableColumn) {
		if (column.info.fixedCellClass.indexOf(c) === -1) {
			column.info.fixedCellClass.push(c);
		}
		column.cellClass = column.cellClass || '';
		if (typeof(column.cellClass) === 'string') {
			column.cellClass += this.fixedCellClassToString(column.info.fixedCellClass);
		} else if (typeof(column.cellClass) === 'function') {

			// any cellClass function should already consume fixedCellClass

		}
	}
	fixedCellClassToString(fixed: string[]) {
		return ' ' + fixed.join(' ') + ' '
	}

	getSmartDisplay(col: { values; header; isDisplay; type; } , numOfCols) {
		let width;
		let align: 'C' | 'R' | 'L';
		if (col.isDisplay) {
			const display = LB.TableUtility.smartDisplay(col.values, col.header, col.type);
			const isAllNull = !col.values.find(v => !!v);
			// If all values are null, use fixed width
			if (isAllNull) {
				width = 60;
			} else {
				if (display.size <= 4) { width = 50; }
				else if (display.size <= 20) { width = 50 + display.size * 3; }
				else if (display.size <= 50) {
					// This Is Very Rough Estimation!!! Should be improved.
					if (numOfCols <= 5) {
						width = display.size * 7;
					} else {
						width = 200;
					}
				} else {
					width = 300;
				}
			}
			switch (display.alignment) {
				case 'center': align = 'C'; break;
				case 'left': align = 'L'; break;
				case 'right': align = 'R'; break;
			}
		}
		return {
			width: width,
			align: align
		}
	}
	// Update columns width & align with smartDisplay for explore table & batch table
	public updateColumnsSmartDisplay(rows: any[], columnsAll: TableColumn[]) {
		const colsByProp = {};
		rows.forEach(r => {
			for (const prop of Object.keys(r)) {
				if (!colsByProp[prop]) {
					colsByProp[prop] = {
						values: [],
						header: prop,
						type: null, // will be updated with columns value
						isDisplay: true
					};
				}
				colsByProp[prop].values.push(r[prop]);
			}
		});
		// Use eventColumnsAll here to smart display for all columns
		const numOfCols = columnsAll.length;
		columnsAll.forEach((c) => {
			if (c.info.skipSmartDisplay) {
				return;
			}
			const col = colsByProp[c.prop];
			if (!col) { 
				if (c.isDisplay) {
					c.width = 30; // set a small width for that empty col
				}
				return;
			}
			col.type = c.type;
			// For date column, if values are string, set the col type as string
			if (c.type === 'date') {
				if (col.values[0] && typeof(col.values[0]) === 'string') {
					col.type = 'string';
				}
			}
			const display = this.getSmartDisplay(col, numOfCols);
			c.width = display.width;
			if (display.align) {
				// Update column align
				const alignClass = 'cell-align-' + display.align;
				this.addFixedCellClass(alignClass, c);
				// Update header align
				this.updateColumnHeaderAlign(c, display.align);
			}
		});
	}
	extendTableFull(elemRef: ElementRef, tableId: string, columns: TableColumn[], forceCurrentView?: boolean) {
		let widthTotal = 0;
		let extendColWidthTotal = 0;
		// const headerElems = elemRef.nativeElement.querySelectorAll(`#${tableId} datatable-header-cell`);
		// headerElems.forEach(e => {
		// 	widthTotal += e.getBoundingClientRect().width;
		// });
		const filtered = columns.filter(c => {
			if (c.isDisplay !== false && c.width) {
				widthTotal += c.width;
				if (!c.info.skipSmartDisplay) {
					extendColWidthTotal += c.width;
					return true;
				}
			}
		});
		const tableElem = elemRef.nativeElement.querySelector(`#${tableId} datatable-body`);
		const tableWidth = tableElem.getBoundingClientRect().width;
		if (widthTotal < tableWidth || forceCurrentView) {
			const diff = tableWidth - widthTotal;
			// let adjustableWidthTotal = 0;
			// columns.forEach(c => {
			// 	if (c.width) {
			// 		adjustableWidthTotal += c.width;
			// 	}
			// });
			filtered.forEach(c => {
				if (c.width) {
					c.width = c.width + c.width / extendColWidthTotal * diff;
					// c.width = c.width + c.width / adjustableWidthTotal * diff;
				}
			});
		}
		return columns;
	}

	public getColumnByProp(columns: TableColumn[], prop: string) {
		let col: TableColumn;
		for (let i = 0; i < columns.length; i++) {
			if (columns[i].prop.toLowerCase() === prop) {
				col = columns[i];
				break;
			}
		}
		return col;
	}
	// setColumnsFromRow(row: TableRow, columnsMaxStringLen?: {[colName: string]: number}): TableColumn[] {
	// 	const columns: TableColumn[] = [];
	// 	if (row) {
	// 		columnsMaxStringLen = columnsMaxStringLen ? columnsMaxStringLen : {};
	// 		for (const name of Object.keys(row)) {
	// 			const column = this.setNewColumn(name, name, columnsMaxStringLen[name]);
	// 			columns.push(column);
	// 		}
	// 	}
	// 	return columns.sort(this.columnSort);
	// }
	// Get table prefix from a row
	getTablePrefixByRow(data: TableRow): string {
		for (const col of Object.keys(data)) {
			const prefix = this.getColPrefix(col);
			if (prefix) {
				return prefix;
			}
		}
	}
	getColPrefix(col: string) {
		// Index of dash in column prop
		const idxOfDash = col.indexOf('_');
		if (idxOfDash === 3) {
			return col.substring(0, 3);
		}
	}
	//
	calcPageSize(containerH, footerH, headerH, rowH): number {
		let pageSize = Math.floor((containerH - footerH - headerH) / this.defaultTableConfig.rowH) - 1;
		if (!pageSize) { pageSize = 10; }
		return pageSize;
	}
	copyToClipboard(text: string) {
		if (document.getElementById(this.copyId)) {
			document.getElementById(this.copyId).remove();
		}
		const elem = document.createElement('textarea');
		elem.setAttribute('id', this.copyId);
		document.body.appendChild(elem);
		elem.value = text;
		elem.select();
	}
	hideColumn(col: TableColumn) {
		col.isDisplay = false;
		col.width = 0;
		col.minWidth = 0;
		this.addHeaderClass(col, 'hide-column');
		this.addFixedCellClass(' hide-cell ', col);
	}
	// Move column to front // e.g. nguid
	moveColToFront = (cols: TableColumn[], colProp, frozen?: boolean) => {
		const colIdx = cols.findIndex(c => c.prop === colProp);
		if (colIdx > -1) {
			const col = cols[colIdx];
			if (frozen) {
				// col.frozenLeft = true;
			}
			cols.splice(colIdx, 1);
			cols.unshift(col);
		}	
	}
	private addHeaderClass(col: TableColumn, headerClass: string) {
		col.headerClass = col.headerClass ? col.headerClass + ' ' + headerClass : headerClass;
	}
	// Column sort function
	columnSort(colA: {seq?: number}, colB: {seq?: number}) {
		if (colA.seq === undefined) {
			if (colB.seq === undefined) {
				return 0;
			} else {
				return 1;
			}
		} else if (colB.seq === undefined) {
			return -1;
		} else {
			return colA.seq > colB.seq ? 1 : -1;
		}
	}

	getRowsInPage(pageNum, rowsAll, pageSize) {
		if (pageNum < 1) {
			return [];
		}
		const startIdx = pageSize * (pageNum - 1);
		const endIdx = startIdx + pageSize;
		return rowsAll.slice(startIdx, endIdx);
	}
}
